import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { MapPin, ShoppingBag, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Step 1: Order type selection (delivery / pickup) + address form.
 * Shown only for non-table orders.
 */
export default function CheckoutStep1({ tc, restaurant, orderType, onOrderTypeChange, delivery, onDeliveryChange, onClose, onNext }) {
  const [pr, pg, pb] = tc.primary.replace("#","").match(/.{2}/g).map(h => parseInt(h,16));
  const linkedCoupon = (() => {
    try { return !!new URLSearchParams(window.location.search).get("coupon"); }
    catch { return false; }
  })();

  const handleNext = () => {
    if (orderType === "delivery" && (!delivery.address || !delivery.number)) {
      toast.error("Endereco e numero obrigatorios");
      return;
    }
    if (orderType === "delivery" && restaurant?.delivery_zones?.some(z => z.active !== false && z.free_above != null) && (delivery.lat == null || delivery.lng == null)) {
      toast.error("Seleccione una dirección de la lista para calcular el envío");
      return;
    }
    onNext();
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-lg font-bold">Como deseja receber?</DialogTitle>
      </DialogHeader>

      <div className="space-y-4">
        {/* Delivery / Pickup toggle */}
        <div className="grid grid-cols-2 gap-2" data-testid="order-type-toggle">
          {restaurant?.delivery_enabled !== false && !linkedCoupon && (
            <button onClick={() => onOrderTypeChange("delivery")}
              className={`p-3 rounded-xl border-2 text-center font-semibold text-sm transition ${orderType === "delivery" ? "bg-opacity-10" : "border-gray-200 bg-gray-50 text-gray-500"}`}
              style={orderType === "delivery" ? { borderColor: tc.primary, backgroundColor: `rgba(${pr},${pg},${pb},0.06)`, color: tc.primary } : {}}
              data-testid="order-type-delivery">
              <MapPin className="w-5 h-5 mx-auto mb-1" style={{ color: orderType === "delivery" ? tc.primary : undefined }} />
              Delivery
            </button>
          )}
          {restaurant?.pickup_enabled !== false && (
            <button onClick={() => onOrderTypeChange("pickup")}
              className={`p-3 rounded-xl border-2 text-center font-semibold text-sm transition ${orderType === "pickup" ? "bg-opacity-10" : "border-gray-200 bg-gray-50 text-gray-500"}`}
              style={orderType === "pickup" ? { borderColor: tc.primary, backgroundColor: `rgba(${pr},${pg},${pb},0.06)`, color: tc.primary } : {}}
              data-testid="order-type-pickup">
              <ShoppingBag className="w-5 h-5 mx-auto mb-1" style={{ color: orderType === "pickup" ? tc.primary : undefined }} />
              Retirada
            </button>
          )}
        </div>
        {linkedCoupon && (
          <p className="text-xs text-center font-medium" style={{ color: tc.primary }} data-testid="coupon-pickup-lock">
            Esta oferta é exclusiva para retirada no local.
          </p>
        )}

        {/* Address form — delivery only */}
        {orderType === "delivery" && (
          <>
            <div className="rounded-xl overflow-hidden border h-40 bg-gray-50">
              {delivery.lat && delivery.lng ? (
                <iframe title="Ubicacion" width="100%" height="100%" frameBorder="0" style={{ border: 0 }}
                  src={`https://www.openstreetmap.org/export/embed.html?bbox=${delivery.lng-0.005},${delivery.lat-0.005},${delivery.lng+0.005},${delivery.lat+0.005}&layer=mapnik&marker=${delivery.lat},${delivery.lng}`}
                  allowFullScreen />
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                  <MapPin className="w-5 h-5 mr-2" style={{ color: tc.primary }} />
                  Informe seu endereco para ver o mapa
                </div>
              )}
            </div>
            <div className="relative">
              <AddressAutocomplete
                value={delivery.address}
                onChange={value => { onDeliveryChange.address(value); onDeliveryChange.lat(null); onDeliveryChange.lng(null); }}
                onSelect={({ address, lat, lng }) => {
                  onDeliveryChange.address(address);
                  onDeliveryChange.lat(lat);
                  onDeliveryChange.lng(lng);
                }}
                placeholder="Buscar endereco..."
                className="bg-gray-50"
                data-testid="delivery-address-input" />
              {delivery.address && (
                <button
                  type="button"
                  onClick={() => { onDeliveryChange.address(""); onDeliveryChange.lat(null); onDeliveryChange.lng(null); onDeliveryChange.number(""); onDeliveryChange.complement(""); onDeliveryChange.barrio(""); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 transition"
                  data-testid="clear-address-btn"
                  title="Limpar endereço">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input value={delivery.number} onChange={e => onDeliveryChange.number(e.target.value)} placeholder="Numero *" className="bg-gray-50" data-testid="address-number" />
              <Input value={delivery.complement} onChange={e => onDeliveryChange.complement(e.target.value)} placeholder="Complemento" className="bg-gray-50" />
            </div>
            <Input value={delivery.barrio} onChange={e => onDeliveryChange.barrio(e.target.value)} placeholder="Zona / Localidade" className="bg-gray-50" data-testid="address-barrio" />
          </>
        )}

        {/* Pickup address display */}
        {orderType === "pickup" && restaurant?.address && (
          <div className="p-4 rounded-xl border"
            style={{ backgroundColor: `rgba(${pr},${pg},${pb},0.06)`, borderColor: `rgba(${pr},${pg},${pb},0.25)` }}>
            <p className="text-sm font-medium" style={{ color: tc.primary }}>Retirar em:</p>
            <p className="text-sm text-gray-600 mt-1">{restaurant.address}</p>
          </div>
        )}
      </div>

      <DialogFooter className="gap-2">
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
        <Button onClick={handleNext} className="text-white font-semibold" style={{ backgroundColor: tc.primary }} data-testid="confirm-address-btn">
          {orderType === "pickup" ? "Continuar" : "Confirmar Endereco"}
        </Button>
      </DialogFooter>
    </>
  );
}
